import { useEffect, useState } from 'react';
import { Minus, Plus, Wand2 } from 'lucide-react';
import {
  MAX_PLAYERS,
  MIN_PLAYERS,
  ROLES,
  ROLE_INFO,
  defaultRoles,
  DEFAULT_TIMERS,
  isMafiaTeam,
  validateSettings,
  type GameSettings,
  type LobbyView,
  type PhaseTimers,
  type RoleId,
} from '@mafia/core';
import { emit } from '@/lib/socket';
import { Alert, Button } from '@/components/ui';

type SpecialRole = Exclude<RoleId, 'CIVILIAN'>;
const SPECIAL: SpecialRole[] = ['MAFIA', 'DON', 'SHERIFF', 'DOCTOR', 'LOVER', 'MANIAC'];

const TIMER_LABELS: Record<keyof PhaseTimers, string> = {
  roleRevealSec: 'Знакомство с ролью',
  nightSec: 'Ночь',
  morningSec: 'Утро: каждая анимация (убийство, любовница)',
  speechSec: 'Речь игрока',
  tieSpeechSec: 'Оправдание при ничьей',
  votingSec: 'Голосование',
  lastWordSec: 'Последнее слово',
  discussionSec: 'Общее обсуждение (0 — без него)',
};

type ToggleKey = 'openVoting' | 'revealRoleOnDeath' | 'nightVictimLastWord' | 'nightEndsEarly' | 'skipVoteOnce';
const TOGGLES: { key: ToggleKey; label: string; hint: string }[] = [
  { key: 'skipVoteOnce', label: 'Пропуск голосования', hint: 'Стол может один раз за игру решить никого не исключать' },
  { key: 'openVoting', label: 'Открытое голосование', hint: 'Все видят, кто за кого голосует' },
  { key: 'nightVictimLastWord', label: 'Последнее слово убитым ночью', hint: 'По клубным правилам убитый мафией его не имеет' },
  { key: 'revealRoleOnDeath', label: 'Раскрывать роль после смерти', hint: 'Меньше тайны, проще новичкам' },
  { key: 'nightEndsEarly', label: 'Досрочный конец ночи', hint: 'Быстрее, но по длине ночи можно догадаться, кто жив' },
];

/** Игроков с ролями: все участники, кроме ведущего без роли. */
export const playerCount = (lobby: LobbyView) => lobby.members.length - (lobby.moderated ? 1 : 0);
/** Сколько игроков брать для подсказок: текущий состав, но не меньше минимума. */
const plannedCount = (lobby: LobbyView) => Math.max(playerCount(lobby), MIN_PLAYERS);

export function RoleSummary({ settings, players }: { settings: GameSettings; players: number }) {
  const special = SPECIAL.reduce((a, r) => a + (settings.roles[r] ?? 0), 0);
  const civilians = Math.max(0, players - special);
  return (
    <div className="flex flex-wrap gap-1.5">
      {SPECIAL.filter((r) => (settings.roles[r] ?? 0) > 0).map((r) => (
        <span key={r} className={`rounded-lg px-2 py-1 text-xs ${isMafiaTeam(r) ? 'bg-blood-600/15 text-blood-400' : r === 'MANIAC' ? 'bg-purple-500/15 text-purple-300' : 'bg-indigo-500/15 text-indigo-300'}`}>
          {ROLE_INFO[r].emoji} {ROLE_INFO[r].name} ×{settings.roles[r]}
        </span>
      ))}
      <span className="rounded-lg bg-white/5 px-2 py-1 text-xs text-zinc-300">
        {ROLE_INFO.CIVILIAN.emoji} Мирные ×{civilians}
      </span>
    </div>
  );
}

export function SettingsSummary({ lobby }: { lobby: LobbyView }) {
  const s = lobby.settings;
  return (
    <div className="space-y-4 text-sm">
      <div>
        <p className="mb-2 text-zinc-500">Роли при {plannedCount(lobby)} игроках</p>
        <RoleSummary settings={s} players={plannedCount(lobby)} />
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {(Object.keys(TIMER_LABELS) as (keyof PhaseTimers)[]).map((k) => (
          <div key={k} className="contents">
            <dt className="text-zinc-500">{TIMER_LABELS[k]}</dt>
            <dd className="text-right font-mono">{s.timers[k] ?? DEFAULT_TIMERS[k]} с</dd>
          </div>
        ))}
      </dl>
      <ul className="space-y-1 text-zinc-400">
        {TOGGLES.map((t) => (
          <li key={t.key}>
            {s[t.key] ? '✓' : '✗'} {t.label}
          </li>
        ))}
        <li>
          {lobby.moderated ? '✓ Ведущий без роли' : '✗ Без ведущего'} ·{' '}
          {s.pace === 'manual' ? 'ручной режим (без таймеров)' : 'таймеры'}
        </li>
        <li>Самолечение доктора: {s.doctorSelfHeals} раз(а)</li>
      </ul>
    </div>
  );
}

export function SettingsEditor({ lobby, canModerate }: { lobby: LobbyView; canModerate: boolean }) {
  const [draft, setDraft] = useState<GameSettings>(withDefaults(lobby.settings));
  const [maxPlayers, setMax] = useState(lobby.maxPlayers);
  const [moderated, setModerated] = useState(lobby.moderated);
  const [saving, setSaving] = useState(false);

  // Чужие изменения (или ответ сервера) приходят через room:state — синхронизируем черновик.
  useEffect(() => {
    setDraft(withDefaults(lobby.settings));
    setMax(lobby.maxPlayers);
    setModerated(lobby.moderated);
  }, [lobby.settings, lobby.maxPlayers, lobby.moderated]);

  const dirty =
    JSON.stringify(draft) !== JSON.stringify(withDefaults(lobby.settings)) ||
    maxPlayers !== lobby.maxPlayers ||
    moderated !== lobby.moderated;
  const players = plannedCount(lobby);
  const issues = validateSettings(draft, players).filter((i) => !i.startsWith('players must'));

  const setRole = (role: SpecialRole, delta: number) => {
    const next = Math.max(0, Math.min(ROLES[role].maxCount, (draft.roles[role] ?? 0) + delta));
    setDraft({ ...draft, roles: { ...draft.roles, [role]: next } });
  };

  async function save() {
    setSaving(true);
    await emit('room:updateSettings', { maxPlayers, settings: draft, moderated });
    setSaving(false);
  }

  return (
    <div className="space-y-5">
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-medium text-zinc-300">Мест в комнате: {maxPlayers}</h3>
        </div>
        <input
          type="range"
          min={Math.max(MIN_PLAYERS, playerCount(lobby))}
          max={MAX_PLAYERS}
          value={maxPlayers}
          onChange={(e) => setMax(Number(e.target.value))}
          className="w-full accent-blood-500"
          aria-label="Мест в комнате"
        />
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-zinc-300">Роли</h3>
          <button
            className="flex items-center gap-1 text-xs text-gold-300 hover:text-gold-400"
            onClick={() => setDraft({ ...draft, roles: defaultRoles(players) })}
          >
            <Wand2 className="h-3.5 w-3.5" /> Подобрать под {players}
          </button>
        </div>
        <div className="space-y-1.5">
          {SPECIAL.map((role) => (
            <div key={role} className="flex items-center justify-between rounded-xl bg-ink-850 px-3 py-2">
              <span className="text-sm">
                {ROLE_INFO[role].emoji} {ROLE_INFO[role].name}
              </span>
              <div className="flex items-center gap-2">
                <button className="rounded-lg p-1 hover:bg-white/10 disabled:opacity-30" onClick={() => setRole(role, -1)} disabled={!draft.roles[role]} aria-label={`Меньше: ${ROLE_INFO[role].name}`}>
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-5 text-center font-mono">{draft.roles[role] ?? 0}</span>
                <button
                  className="rounded-lg p-1 hover:bg-white/10 disabled:opacity-30"
                  onClick={() => setRole(role, 1)}
                  disabled={(draft.roles[role] ?? 0) >= ROLES[role].maxCount}
                  aria-label={`Больше: ${ROLE_INFO[role].name}`}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3">
          <p className="mb-1.5 text-xs text-zinc-500">Раздача при {players} игроках:</p>
          <RoleSummary settings={draft} players={players} />
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-medium text-zinc-300">Таймеры, секунд</h3>
        <div className="grid grid-cols-2 gap-2">
          {(Object.keys(TIMER_LABELS) as (keyof PhaseTimers)[]).map((k) => (
            <label key={k} className="block">
              <span className="mb-1 block text-xs text-zinc-500">{TIMER_LABELS[k]}</span>
              <input
                type="number"
                min={k === 'discussionSec' ? 0 : 3}
                max={600}
                className="input py-1.5 font-mono"
                value={draft.timers[k]}
                onChange={(e) => setDraft({ ...draft, timers: { ...draft.timers, [k]: Number(e.target.value) || 0 } })}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-zinc-300">Ведение игры</h3>
        <label className={`flex items-start gap-3 rounded-xl bg-ink-850 px-3 py-2.5 ${canModerate ? 'cursor-pointer' : 'opacity-50'}`}>
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-gold-400"
            disabled={!canModerate}
            checked={moderated}
            onChange={(e) => {
              setModerated(e.target.checked);
              if (!e.target.checked && draft.pace === 'manual') setDraft({ ...draft, pace: 'auto' });
            }}
          />
          <span>
            <span className="block text-sm">Я ведущий — не играю, управляю партией</span>
            <span className="block text-xs text-zinc-500">
              {canModerate ? 'Вы увидите все роли и ночные ходы, сможете переключать фазы и удалять игроков' : 'Доступно только администраторам сайта'}
            </span>
          </span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {(['auto', 'manual'] as const).map((pace) => (
            <button
              key={pace}
              type="button"
              disabled={pace === 'manual' && !moderated}
              onClick={() => setDraft({ ...draft, pace })}
              className={`rounded-xl px-3 py-2.5 text-left text-sm transition disabled:opacity-40 ${
                draft.pace === pace ? 'bg-gold-400/15 text-gold-300 ring-1 ring-gold-400/40' : 'bg-ink-850 text-zinc-300 hover:bg-ink-800'
              }`}
            >
              <span className="block font-medium">{pace === 'auto' ? 'Таймеры' : 'Ручной режим'}</span>
              <span className="block text-xs text-zinc-500">{pace === 'auto' ? 'Фазы идут по времени' : 'Без таймеров, фазы переключает ведущий'}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-zinc-300">Правила</h3>
        {TOGGLES.map((t) => (
          <label key={t.key} className="flex cursor-pointer items-start gap-3 rounded-xl bg-ink-850 px-3 py-2.5">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-gold-400"
              checked={draft[t.key]}
              onChange={(e) => setDraft({ ...draft, [t.key]: e.target.checked })}
            />
            <span>
              <span className="block text-sm">{t.label}</span>
              <span className="block text-xs text-zinc-500">{t.hint}</span>
            </span>
          </label>
        ))}
        <label className="flex items-center justify-between rounded-xl bg-ink-850 px-3 py-2.5">
          <span className="text-sm">Доктор может лечить себя (раз за игру)</span>
          <input
            type="number"
            min={0}
            max={10}
            className="input w-16 py-1 text-center font-mono"
            value={draft.doctorSelfHeals}
            onChange={(e) => setDraft({ ...draft, doctorSelfHeals: Math.max(0, Math.min(10, Number(e.target.value) || 0)) })}
          />
        </label>
      </section>

      {issues.length > 0 && (
        <Alert tone="gold">
          {issues.map((i) => (
            <div key={i}>{translateIssue(i)}</div>
          ))}
        </Alert>
      )}

      <div className="sticky bottom-0 -mx-1 flex gap-2 bg-gradient-to-t from-ink-900 via-ink-900 to-transparent px-1 pb-1 pt-3">
        <Button className="flex-1" variant="gold" disabled={!dirty} loading={saving} onClick={save}>
          Сохранить
        </Button>
        {dirty && (
          <Button
            variant="ghost"
            onClick={() => {
              setDraft(lobby.settings);
              setMax(lobby.maxPlayers);
            }}
          >
            Отменить
          </Button>
        )}
      </div>
    </div>
  );
}

/** Настройки комнат, созданных до появления новых полей. */
function withDefaults(s: GameSettings): GameSettings {
  // Комнаты, созданные до появления общего обсуждения, получают его по умолчанию.
  return { ...s, pace: s.pace ?? 'auto', skipVoteOnce: s.skipVoteOnce ?? true, timers: { ...DEFAULT_TIMERS, ...s.timers } };
}

export function translateIssue(issue: string): string {
  if (issue.startsWith('at least one mafia')) return 'Нужна хотя бы одна мафия';
  if (issue.startsWith('mafia must be less than half')) return 'Мафии должно быть меньше половины стола';
  if (issue.startsWith('more special roles')) return 'Спец-ролей больше, чем игроков';
  if (issue.startsWith('players must')) return `Нужно от ${MIN_PLAYERS} до ${MAX_PLAYERS} игроков`;
  if (issue.includes('seconds')) return 'Таймеры: от 3 до 600 секунд';
  return issue;
}
